import { QueryClient, queryOptions } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { allCategoriesQueryOptions } from "../categories/allCategoriesQuery";
import { CourseData, SavedCourse, ShallowCourse } from "@vixi/types";
import { api } from "@/apiInstance";
import { endpoints } from "@/endpoints";
import { findCategory, savedCourseToCourse } from "@vixi/utils";

export const courseBaseByIdQueryOptions = (queryClient: QueryClient, courseId?: string | number) =>
  queryOptions({
    queryKey: ["courseBase", courseId],
    queryFn: async () => {
      const [response, categories] = await Promise.all([
        api.get(endpoints.getCourseBase, {
          params: {
            CourseId: courseId,
          },
        }) as Promise<AxiosResponse<ShallowCourse>>,
        queryClient.ensureQueryData(allCategoriesQueryOptions()),
      ]);

      const { units, categoryId, ...course } = response.data;
      const [category, subcategory] = findCategory(categoryId, categories);

      const res: ShallowCourse = {
        ...course,
        categoryId,
        category,
        subcategory,
      };

      // Update base course if it exists in the cache
      if (queryClient.getQueryData<CourseData.Course>(["course", courseId])) {
        queryClient.setQueryData<CourseData.Course>(["course", courseId], (old) => ({
          ...old,
          ...res,
        }));
      }

      return res;
    },
    enabled: !!courseId,
    refetchOnMount: false,
  });

export const courseByIdQueryOptions = (queryClient: QueryClient, courseId: string | number) =>
  queryOptions({
    queryKey: ["course", courseId],
    queryFn: async ({ queryKey: [, courseId], signal }) => {
      const [response, categories] = await Promise.all([
        api.get(endpoints.getCourse, {
          signal,
          params: {
            CourseId: courseId,
          },
        }) as Promise<AxiosResponse<Required<SavedCourse.Course>>>,
        queryClient.ensureQueryData(allCategoriesQueryOptions()),
      ]);

      const course = savedCourseToCourse(response.data, categories);

      return course;
    },
    refetchOnMount: false,
  });
